import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Receipt, Trash2, Pencil, DollarSign, FileText, Car, ShoppingCart, User, UserCheck, Users, PieChart, Calculator, Wallet, ClipboardList, Bookmark, AlertCircle, CreditCard, Info, Search, UserCircle2, X, Hourglass, CheckCircle2, Circle, RefreshCw, Printer, Link as LinkIcon, Copy } from 'lucide-react';
import { api } from '../lib/api';
import { formatBRL, formatDateBR, formatDateShortBR, formatPercent, todayISO, EXPENSE_CATEGORIES, FUEL_TYPES, ENTRY_TYPES, PAYMENT_METHODS, PAYMENT_METHOD_ICONS, VEHICLE_STATUS, sortSalePayments } from '../lib/format';
import PageHeader from '../components/PageHeader';
import StatusStepper from '../components/StatusStepper';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { AddressFields } from '../components/AddressFields';
import { Separator } from '../components/ui/separator';
import CurrencyInput from '../components/CurrencyInput';
import PhoneInput from '../components/PhoneInput';
import DocumentInput from '../components/DocumentInput';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { formatPhoneBR } from '../components/PhoneInput';
import { formatDocBR } from '../components/DocumentInput';
import TradeVehicleDialog from '../components/TradeVehicleDialog';
import PaymentDialog from '../components/PaymentDialog';
import CancelReservationDialog from '../components/CancelReservationDialog';
import PersonPickerDialog from '../components/PersonPickerDialog';

function getMissingContractFields(v) {
  const missing = [];
  if (!v) return ['Veículo não carregado'];
  // Vehicle fields
  if (!v.brand) missing.push('Marca');
  if (!v.model) missing.push('Modelo');
  if (!v.year) missing.push('Ano do modelo');
  if (!v.year_fabrication) missing.push('Ano de fabricação');
  if (!v.fuel_type) missing.push('Combustível');
  if (!v.color) missing.push('Cor');
  if (!v.km && v.km !== 0) missing.push('Quilometragem');
  if (!v.plate) missing.push('Placa');
  if (!v.chassis) missing.push('Chassi');
  if (!v.renavam) missing.push('Renavam');
  // Buyer
  if (!v.buyer_name) missing.push('Nome do comprador');
  if (!v.buyer_document) missing.push('CPF/CNPJ do comprador');
  if (!v.buyer_phone) missing.push('Telefone do comprador');
  if (!v.buyer_email) missing.push('E-mail do comprador');
  // Sale
  if (!v.sale_price || v.sale_price <= 0) missing.push('Preço de venda');
  if (!v.sale_date) missing.push('Data da venda');
  const payments = v.sale_payments || [];
  if (payments.length === 0) {
    missing.push('Formas de pagamento');
  } else {
    const sum = payments.reduce((s, p) => s + (Number(p.amount) || 0), 0);
    if (Math.abs(sum - (v.sale_price || 0)) > 0.01) {
      missing.push('Total das formas de pagamento não confere com o valor da venda');
    }
  }
  return missing;
}

function PrintContractButton({ vehicle, id }) {
  const missing = getMissingContractFields(vehicle);
  const disabled = missing.length > 0;
  const btn = (
    <Button
      variant="outline"
      size="sm"
      disabled={disabled}
      onClick={disabled ? undefined : () => window.open(`/veiculos/${id}/contrato`, '_blank', 'noopener,noreferrer')}
      className={`h-auto flex-col gap-1 px-3 py-2 ${disabled ? 'opacity-60 cursor-not-allowed' : 'bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100'}`}
      data-testid="vehicle-print-contract-button"
    >
      <Printer className="h-5 w-5" />
      <span className="text-[10px] font-medium leading-tight">Contrato</span>
    </Button>
  );
  if (!disabled) return btn;
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild><span tabIndex={0}>{btn}</span></TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="text-xs font-semibold mb-1">Faltam preencher:</div>
          <ul className="text-xs list-disc pl-4 space-y-0.5">
            {missing.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function PrintTermButton({ vehicle, id }) {
  const missing = getMissingContractFields(vehicle);
  const disabled = missing.length > 0;
  const btn = (
    <Button
      variant="outline"
      size="sm"
      disabled={disabled}
      onClick={disabled ? undefined : () => window.open(`/veiculos/${id}/termo`, '_blank', 'noopener,noreferrer')}
      className={`h-auto flex-col gap-1 px-3 py-2 ${disabled ? 'opacity-60 cursor-not-allowed' : 'bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100'}`}
      data-testid="vehicle-print-term-button"
    >
      <Printer className="h-5 w-5" />
      <span className="text-[10px] font-medium leading-tight">Termo</span>
    </Button>
  );
  if (!disabled) return btn;
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild><span tabIndex={0}>{btn}</span></TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs">
          <div className="text-xs font-semibold mb-1">Faltam preencher:</div>
          <ul className="text-xs list-disc pl-4 space-y-0.5">
            {missing.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function LeadLinkButton({ vehicleId, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(false);

  const openModal = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const { data } = await api.post(`/vehicles/${vehicleId}/lead-link`);
      setLink(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao gerar link');
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const publicUrl = link ? `${window.location.origin}/p/lead/${link.token}` : '';

  const copyLink = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(() => toast.success('Link copiado'));
  };

  const expiresLabel = () => {
    if (!link?.expires_at) return '';
    const d = new Date(link.expires_at);
    return d.toLocaleString('pt-BR');
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={openModal}
        className="h-auto flex-col gap-1 px-3 py-2 bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100"
        data-testid="vehicle-lead-link-button"
      >
        <LinkIcon className="h-5 w-5" />
        <span className="text-[10px] font-medium leading-tight">Lead</span>
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Link público para o comprador preencher os dados</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">
              Envie este link ao comprador. Ele poderá preencher os próprios dados pessoais e endereço,
              que serão automaticamente vinculados a este veículo e ao cadastro de pessoas.
            </div>
            {loading && <div className="text-sm">Gerando link…</div>}
            {!loading && link && (
              <>
                <div className="flex gap-2 items-center">
                  <Input value={publicUrl} readOnly data-testid="lead-link-url" />
                  <Button variant="outline" size="sm" onClick={copyLink} data-testid="lead-link-copy">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-1">
                  <div>⏱️ <strong>Validade:</strong> 30 minutos. Expira em {expiresLabel()}.</div>
                  <div>✋ <strong>Uso único:</strong> ao ser utilizado, o link é invalidado.</div>
                  <div>♻️ Se este link expirar ou for usado, gere um novo clicando no botão "Dados do lead" novamente.</div>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function VehicleDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExpense, setShowExpense] = useState(false);
  const [showSell, setShowSell] = useState(false);
  const [showEditSale, setShowEditSale] = useState(false);
  const [showReserve, setShowReserve] = useState(false);
  const [showCancelReservation, setShowCancelReservation] = useState(false);
  const [showUndoSale, setShowUndoSale] = useState(false);
  const [undoTargetStatus, setUndoTargetStatus] = useState('AVAILABLE');
  const [keepTrades, setKeepTrades] = useState(false);
  const [linkedTradeVehicles, setLinkedTradeVehicles] = useState([]);
  const [showDelete, setShowDelete] = useState(false);
  const [expenseEdit, setExpenseEdit] = useState(null);
  const [accountsMap, setAccountsMap] = useState({});
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'resumo');
  const [postSaleExpenses, setPostSaleExpenses] = useState([]);
  const [showPostSaleExpense, setShowPostSaleExpense] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [summary, accountsRes] = await Promise.all([
        api.get(`/vehicles/${id}/summary`),
        api.get('/accounts').catch(() => ({ data: [] })),
      ]);
      setData(summary.data);
      const map = {};
      (accountsRes.data || []).forEach((a) => { map[a.id] = a; });
      setAccountsMap(map);
      
      // Load post-sale expenses if vehicle is SOLD
      if (summary.data.vehicle.status === 'SOLD') {
        const expensesRes = await api.get(`/vehicles/${id}/post-sale-expenses`).catch(() => ({ data: [] }));
        setPostSaleExpenses(expensesRes.data || []);
      }
    } catch (err) {
      toast.error('Erro ao carregar veículo');
      navigate('/veiculos');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success('Veículo excluído');
      navigate('/veiculos');
    } catch (err) {
      toast.error('Erro ao excluir');
    }
  };

  const handleCreatePostSaleExpense = async (expenseData) => {
    try {
      const res = await api.post(`/vehicles/${id}/post-sale-expenses`, expenseData);
      toast.success('Despesa pós-venda registrada e distribuída entre os sócios');
      
      // Show distribution summary
      if (res.data.distributions && res.data.distributions.length > 0) {
        const summary = res.data.distributions.map(d => 
          `${d.partner_name}: ${formatBRL(d.expense_amount)} (${d.share_pct}%)`
        ).join('\n');
        console.log('Distribuição:\n' + summary);
      }
      
      setShowPostSaleExpense(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao registrar despesa');
    }
  };

  const handleDeletePostSaleExpense = async (expenseId) => {
    if (!window.confirm('Excluir esta despesa pós-venda? As transações dos sócios serão revertidas.')) return;
    try {
      await api.delete(`/post-sale-expenses/${expenseId}`);
      toast.success('Despesa pós-venda excluída');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao excluir despesa');
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/vehicles/${id}`, { status: newStatus });
      toast.success('Status atualizado');
      load();
    } catch (err) {
      toast.error('Erro ao atualizar status');
    }
  };

  const openUndoSale = async (targetStatus) => {
    setUndoTargetStatus(targetStatus);
    setKeepTrades(false); // reset to default safe choice
    // Fetch linked trade vehicles for the preview
    try {
      const { data: linked } = await api.get('/vehicles', { params: { source_vehicle_id: id } });
      setLinkedTradeVehicles(linked || []);
    } catch (err) {
      setLinkedTradeVehicles([]);
    }
    setShowUndoSale(true);
  };

  const confirmUndoSale = async () => {
    try {
      await api.post(`/vehicles/${id}/undo-sale`, { new_status: undoTargetStatus, keep_trades: keepTrades });
      toast.success(keepTrades ? 'Venda desfeita · Veículos de troca mantidos no estoque' : 'Venda desfeita');
      setShowUndoSale(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao desfazer venda');
    }
  };

  if (loading || !data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  const v = data.vehicle;
  const calc = data.calc;
  const isSold = v.status === 'SOLD';
  const acc = v.account_id ? accountsMap[v.account_id] : null;
  const accountLabel = acc ? `${acc.name}${acc.bank ? ` · ${acc.bank}` : ''}` : null;

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/veiculos')} className="-ml-2" data-testid="vehicle-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Voltar para Veículos
        </Button>
      </div>

      {v.is_stub && (() => {
        const STUB_FIELDS = [
          { key: 'chassis', label: 'chassi' },
          { key: 'renavam', label: 'renavam' },
          { key: 'year_fabrication', label: 'ano de fabricação' },
          { key: 'fuel_type', label: 'combustível' },
          { key: 'km', label: 'KM' },
          { key: 'color', label: 'cor' },
        ];
        const missing = STUB_FIELDS.filter((f) => {
          const val = v[f.key];
          return val === null || val === undefined || val === '' ||
            (typeof val === 'string' && val.trim() === '');
        });
        if (missing.length === 0) return null;
        return (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="stub-banner">
            <AlertCircle className="h-5 w-5 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-amber-900 text-sm">Cadastro pendente</div>
              <div className="text-xs text-amber-800 mt-0.5">
                Complete os {missing.length} campo{missing.length > 1 ? 's' : ''} restante{missing.length > 1 ? 's' : ''}:{' '}
                <strong>{missing.map((m) => m.label).join(', ')}</strong>.
              </div>
            </div>
            <Button size="sm" variant="default" onClick={() => navigate(`/veiculos/${id}/editar`)} className="bg-amber-600 hover:bg-amber-700 text-white">
              Completar dados
            </Button>
          </div>
        );
      })()}

      {v.source_vehicle_id && (
        <div className="mb-4 rounded-lg border border-border/70 bg-card p-3 flex items-center gap-3" data-testid="source-banner">
          <div className="h-9 w-9 rounded-lg bg-muted grid place-items-center"><Car className="h-4 w-4 text-muted-foreground" /></div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-muted-foreground">Origem</div>
            <div className="text-sm">Recebido em troca pela venda de <span className="font-medium">{v.source_vehicle_label || 'outro veículo'}</span></div>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate(`/veiculos/${v.source_vehicle_id}`)}>
            Ver veículo de origem
          </Button>
        </div>
      )}

      <PageHeader
        title={
          <span className="flex items-center gap-2 flex-wrap">
            <span>{v.brand} {v.model} {v.year}</span>
            {v.is_reserved && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-900 border border-amber-200" data-testid="reserved-badge">
                <Bookmark className="h-3 w-3" /> Reservado
              </span>
            )}
            {v.is_direct_passthrough && (
              <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium bg-purple-100 text-purple-900 border border-purple-200" data-testid="passthrough-badge" title="Passagem direta - sem transferência para a empresa">
                <ShoppingCart className="h-3 w-3" /> Passagem direta
              </span>
            )}
          </span>
        }
        subtitle={v.plate ? `Placa ${v.plate}` : v.color || 'Detalhes do veículo'}
        actions={
          <div className="grid grid-cols-4 gap-2 md:flex md:flex-wrap md:grid-cols-none">
            {v.status === 'SOLD' && (
              <Button
                variant="outline"
                onClick={() => setShowEditSale(true)}
                className="h-auto flex-col gap-1 px-3 py-2 bg-emerald-50 border-emerald-200 text-emerald-900 hover:bg-emerald-100"
                data-testid="vehicle-edit-sale-button"
              >
                <DollarSign className="h-5 w-5" />
                <span className="text-[10px] font-medium leading-tight">Venda</span>
              </Button>
            )}
            {!v.is_reserved && (
              <Button
                variant="outline"
                onClick={() => setShowReserve(true)}
                className="h-auto flex-col gap-1 px-3 py-2 bg-emerald-50 border-emerald-200 text-emerald-900 hover:bg-emerald-100"
                data-testid="vehicle-reserve-button"
              >
                <Bookmark className="h-5 w-5" />
                <span className="text-[10px] font-medium leading-tight">Reservar</span>
              </Button>
            )}
            {v.status === 'SOLD' && <PrintContractButton vehicle={v} id={id} />}
            {v.status === 'SOLD' && <PrintTermButton vehicle={v} id={id} />}
            <Button
              variant="outline"
              onClick={() => navigate(`/veiculos/${id}/editar`)}
              className="h-auto flex-col gap-1 px-3 py-2"
              data-testid="vehicle-edit-button"
            >
              <Pencil className="h-5 w-5" />
              <span className="text-[10px] font-medium leading-tight">Editar</span>
            </Button>
            <LeadLinkButton vehicleId={id} />
            <Button
              variant="outline"
              onClick={() => setShowDelete(true)}
              className="h-auto flex-col gap-1 px-3 py-2"
              data-testid="vehicle-delete-button"
            >
              <Trash2 className="h-5 w-5 text-[hsl(var(--danger))]" />
              <span className="text-[10px] font-medium leading-tight text-[hsl(var(--danger))]">Excluir</span>
            </Button>
          </div>
        }
      />

      <div className="mb-6">
        <StatusStepper
          current={v.status}
          onChange={handleStatusChange}
          onSell={() => setShowSell(true)}
          onUndoSale={openUndoSale}
        />
      </div>

      {v.is_reserved && (
        <div className="mb-6 rounded-xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-amber-100/50 p-4" data-testid="reservation-banner">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-amber-200 grid place-items-center shrink-0">
              <Bookmark className="h-5 w-5 text-amber-800" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div>
                  <div className="text-sm font-semibold text-amber-900">Veículo reservado</div>
                  <div className="text-xs text-amber-800">Reserva paralela ao ciclo de vida do veículo</div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setShowReserve(true)} data-testid="reservation-edit-button">
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Editar reserva
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowCancelReservation(true)} className="text-[hsl(var(--danger))] hover:bg-red-50" data-testid="reservation-cancel-button">
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Cancelar reserva
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                {v.reservation_buyer_name && (
                  <div>
                    <div className="text-[11px] text-amber-700 uppercase tracking-wide">Reservante</div>
                    <div className="font-medium text-amber-900 truncate">{v.reservation_buyer_name}</div>
                  </div>
                )}
                {v.reservation_buyer_document && (
                  <div>
                    <div className="text-[11px] text-amber-700 uppercase tracking-wide">CPF / CNPJ</div>
                    <div className="text-amber-900 tabular">{formatDocBR(v.reservation_buyer_document)}</div>
                  </div>
                )}
                {v.reservation_buyer_phone && (
                  <div>
                    <div className="text-[11px] text-amber-700 uppercase tracking-wide">Telefone</div>
                    <div className="text-amber-900 tabular">{formatPhoneBR(v.reservation_buyer_phone)}</div>
                  </div>
                )}
                {v.reservation_deposit > 0 && (
                  <div>
                    <div className="text-[11px] text-amber-700 uppercase tracking-wide">Sinal recebido</div>
                    <div className="font-semibold text-amber-900 tabular">{formatBRL(v.reservation_deposit)}</div>
                  </div>
                )}
                {v.reservation_due_date && (
                  <div>
                    <div className="text-[11px] text-amber-700 uppercase tracking-wide">Prazo</div>
                    <div className="text-amber-900">{formatDateBR(v.reservation_due_date)}</div>
                  </div>
                )}
              </div>
              {v.reservation_notes && (
                <div className="mt-2 pt-2 border-t border-amber-200 text-xs text-amber-800 italic">"{v.reservation_notes}"</div>
              )}
            </div>
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid ${isSold ? 'grid-cols-6' : 'grid-cols-5'} w-full max-w-4xl h-auto`}>
          <TabsTrigger value="resumo" data-testid="tab-resumo" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
            <FileText className="h-4 w-4 shrink-0" />
            <span>Resumo</span>
          </TabsTrigger>
          <TabsTrigger value="composicao" data-testid="tab-composicao" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
            <Users className="h-4 w-4 shrink-0" />
            <span>Composição</span>
          </TabsTrigger>
          <TabsTrigger value="despesas" data-testid="tab-despesas" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
            <Receipt className="h-4 w-4 shrink-0" />
            <span>Despesas</span>
          </TabsTrigger>
          {isSold && (
            <TabsTrigger value="pos-venda" data-testid="tab-pos-venda" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">Pós-Venda</span>
              <span className="sm:hidden">Pós</span>
            </TabsTrigger>
          )}
          <TabsTrigger value="dre" data-testid="tab-dre" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
            <Calculator className="h-4 w-4 shrink-0" />
            <span>DRE</span>
          </TabsTrigger>
          <TabsTrigger value="checklist" data-testid="tab-checklist" className="flex-col sm:flex-row gap-0.5 sm:gap-1.5 py-1.5 text-[11px] sm:text-sm">
            <ClipboardList className="h-4 w-4 shrink-0" />
            <span>Checklist</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="resumo" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Card className="border-border/70 lg:col-span-2">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Car className="h-4 w-4 text-muted-foreground" /> Dados do veículo</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                <Item label="Marca" value={v.brand} />
                <Item label="Modelo" value={v.model} />
                <Item label="Versão" value={v.version || '—'} />
                <Item label="Ano modelo" value={v.year} />
                <Item label="Ano fabricação" value={v.year_fabrication || '—'} />
                <Item label="Combustível" value={FUEL_TYPES[v.fuel_type] || '—'} />
                <Item label="Cor" value={v.color || '—'} />
                <Item label="KM" value={v.km ? `${Number(v.km).toLocaleString('pt-BR')} km` : '—'} />
                <Item label="Placa" value={v.plate || '—'} />
                <Item label="Chassi" value={v.chassis || '—'} />
                <Item label="Renavam" value={v.renavam || '—'} />
              </CardContent>
            </Card>
            <Card className="border-border/70">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Wallet className="h-4 w-4 text-muted-foreground" /> Resumo financeiro</CardTitle></CardHeader>
              <CardContent className="space-y-3 text-sm">
                {v.announced_price && (
                  <Row label="Valor anunciado" value={formatBRL(v.announced_price)} muted />
                )}
                <Row label="Valor negociado" value={formatBRL(calc.purchase_price)} />
                <Row label="Despesas totais" value={formatBRL(calc.total_expenses)} />
                <Row label="Custo total" value={formatBRL(calc.cost_total)} bold />
                {v.reservation_deposit > 0 && (
                  <>
                    <Separator />
                    <Row
                      label="Sinal de reserva recebido"
                      value={`+${formatBRL(v.reservation_deposit)}`}
                      intent="success"
                    />
                  </>
                )}
                {calc.retained_reservation_deposit > 0 && (
                  <>
                    <Separator />
                    <Row
                      label="Sinal retido (reserva cancelada)"
                      value={`+${formatBRL(calc.retained_reservation_deposit)}`}
                      intent="success"
                    />
                  </>
                )}
                {calc.sale_price !== null && (
                  <>
                    <Separator />
                    <Row label="Preço de venda (real)" value={formatBRL(calc.sale_price)} />
                    {v.document_sale_price !== undefined && v.document_sale_price !== null && Math.abs((v.document_sale_price || 0) - (calc.sale_price || 0)) > 0.01 && (
                      <Row label="Valor no documento" value={formatBRL(v.document_sale_price)} muted />
                    )}
                    <Row
                      label="Lucro líquido"
                      value={formatBRL(calc.net_result, { sign: true })}
                      intent={calc.net_result >= 0 ? 'success' : 'danger'}
                      bold
                    />
                    {calc.trade_received > 0 && (
                      <>
                        <Separator />
                        <div className="text-xs text-muted-foreground uppercase tracking-wide pt-1">Distribuição em caixa</div>
                        <Row label="💰 Em dinheiro (distribuível)" value={formatBRL(calc.cash_received)} intent="success" />
                        <Row label="🚗 Em ativo (troca)" value={formatBRL(calc.trade_received)} muted />
                      </>
                    )}
                  </>
                )}
                {calc.is_projection && calc.minimum_sale_price !== null && (
                  <>
                    <Separator />
                    <div className="text-xs text-blue-600 font-medium uppercase tracking-wide pt-1">⚠️ Projeção baseada em valor mínimo</div>
                    <Row label="Valor mínimo de venda" value={formatBRL(calc.minimum_sale_price)} />
                    <Row
                      label="Resultado previsto"
                      value={formatBRL(calc.net_result, { sign: true })}
                      intent={calc.net_result >= 0 ? 'success' : 'danger'}
                      bold
                    />
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="border-border/70">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><ShoppingCart className="h-4 w-4 text-muted-foreground" /> Dados da compra</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <Item label="Data da negociação" value={formatDateBR(v.purchase_date)} />
              <Item label="Tipo de entrada" value={ENTRY_TYPES[v.entry_type] || '—'} />
              <Item label="Forma de pagamento" value={PAYMENT_METHODS[v.payment_method] || '—'} />
              <Item label="Data do pagamento" value={formatDateBR(v.payment_date)} />
              <Item label="Conta de origem" value={accountLabel || '—'} />
              {v.negotiation_notes && (
                <div className="col-span-2 md:col-span-3">
                  <div className="text-xs text-muted-foreground">Observações da negociação</div>
                  <div className="mt-0.5 whitespace-pre-wrap">{v.negotiation_notes}</div>
                </div>
              )}
            </CardContent>
          </Card>

          {isSold && v.sale_payments && v.sale_payments.length > 0 && (
            <Card className="border-border/70">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><CreditCard className="h-4 w-4 text-muted-foreground" /> Composição do pagamento da venda</CardTitle></CardHeader>
              <CardContent>
                <div className="divide-y divide-border/70">
                  {sortSalePayments(v.sale_payments).map((p, i) => (
                    <div key={i} className="flex items-center justify-between py-2.5" data-testid="sale-payment-line">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="text-lg">{PAYMENT_METHOD_ICONS[p.method] || '➖'}</span>
                        <div className="min-w-0">
                          <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                            <span>{PAYMENT_METHODS[p.method] || p.method}</span>
                            {p.date && (() => {
                              const isFuture = p.date > todayISO();
                              return (
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${isFuture ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-muted text-muted-foreground border-border/60'}`}>
                                  {isFuture ? '📅 ' : ''}{formatDateBR(p.date)}{isFuture ? ' · previsto' : ''}
                                </span>
                              );
                            })()}
                          </div>
                          {p.method === 'TRADE' && p.trade_vehicle_id && (
                            <button
                              type="button"
                              onClick={() => navigate(`/veiculos/${p.trade_vehicle_id}`)}
                              className="text-xs text-[hsl(var(--primary))] hover:underline truncate"
                            >
                              🚗 {p.trade_vehicle_label || 'Ver veículo recebido'}
                            </button>
                          )}
                          {p.notes && <div className="text-xs text-muted-foreground">{p.notes}</div>}
                          {Number(p.amount) < 0 && (
                            <div className="text-[10px] font-medium text-red-700 bg-red-50 px-1.5 py-0.5 rounded border border-red-200 w-fit mt-1">
                              💰 Devolução ao comprador
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={`text-sm font-semibold tabular ${Number(p.amount) < 0 ? 'text-red-600' : ''}`}>{formatBRL(p.amount)}</div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-3 mt-1">
                    <div className="text-sm font-semibold">Total recebido</div>
                    <div className="text-sm font-semibold tabular">{formatBRL(v.sale_payments.reduce((s, p) => s + (p.amount || 0), 0))}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {(v.seller_name || v.seller_document || v.seller_phone || v.seller_email || v.seller_info) && (
            <Card className="border-border/70">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><User className="h-4 w-4 text-muted-foreground" /> Dados do vendedor</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {v.seller_name && <Item label="Nome / Razão social" value={v.seller_name} />}
                {v.seller_document && <Item label="CPF / CNPJ" value={formatDocBR(v.seller_document)} />}
                {v.seller_phone && <Item label="Telefone" value={formatPhoneBR(v.seller_phone)} />}
                {v.seller_email && <Item label="E-mail" value={v.seller_email} />}
                {v.seller_info && <div className="col-span-2 md:col-span-4"><div className="text-xs text-muted-foreground">Observações</div><div>{v.seller_info}</div></div>}
              </CardContent>
            </Card>
          )}


          {isSold && (v.buyer_name || v.buyer_document || v.buyer_phone || v.buyer_email || v.buyer_info) && (
            <Card className="border-border/70">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><UserCheck className="h-4 w-4 text-muted-foreground" /> Dados do comprador</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {v.buyer_name && <Item label="Nome / Razão social" value={v.buyer_name} />}
                {v.buyer_document && <Item label="CPF / CNPJ" value={formatDocBR(v.buyer_document)} />}
                {v.buyer_phone && <Item label="Telefone" value={formatPhoneBR(v.buyer_phone)} />}
                {v.buyer_email && <Item label="E-mail" value={v.buyer_email} />}
                <Item label="Data da venda" value={formatDateBR(v.sale_date)} />
                {v.buyer_info && <div className="col-span-2 md:col-span-3"><div className="text-xs text-muted-foreground">Observações</div><div>{v.buyer_info}</div></div>}
              </CardContent>
            </Card>
          )}

          {v.notes && (
            <Card className="border-border/70">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><ClipboardList className="h-4 w-4 text-muted-foreground" /> Observações do veículo</CardTitle></CardHeader>
              <CardContent className="text-sm text-muted-foreground whitespace-pre-wrap">{v.notes}</CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="composicao" className="mt-4">
          <Card className="border-border/70">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><PieChart className="h-4 w-4 text-muted-foreground" /> Composição societária</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="text-left py-2 font-medium">Sócio</th>
                      <th className="text-right py-2 font-medium">% Aporte</th>
                      <th className="text-right py-2 font-medium">Aporte inicial</th>
                      <th className="text-right py-2 font-medium">% Resultado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calc.per_partner.map((p) => (
                      <tr key={p.partner_id} className="border-t border-border/70">
                        <td className="py-3 font-medium">{p.partner_name}</td>
                        <td className="py-3 text-right tabular">{formatPercent(p.contribution_pct)}</td>
                        <td className="py-3 text-right tabular">{formatBRL(p.initial_contribution)}</td>
                        <td className="py-3 text-right tabular">{formatPercent(p.profit_pct)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="despesas" className="mt-4">
          <Card className="border-border/70">
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Receipt className="h-4 w-4 text-muted-foreground" /> Despesas do veículo</CardTitle>
              <Button size="sm" onClick={() => { setExpenseEdit(null); setShowExpense(true); }} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="vehicle-add-expense">
                <Plus className="h-4 w-4 mr-1" /> Nova despesa
              </Button>
            </CardHeader>
            <CardContent className="px-3 md:px-6">
              {data.expenses.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">Nenhuma despesa registrada.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs md:text-sm">
                    <thead className="text-muted-foreground">
                      <tr>
                        <th className="text-left py-2 font-medium">Data</th>
                        <th className="text-left py-2 font-medium hidden md:table-cell">Categoria</th>
                        <th className="text-left py-2 font-medium">Descrição</th>
                        <th className="text-left py-2 font-medium hidden md:table-cell">Pago por</th>
                        <th className="text-right py-2 font-medium">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.expenses.map((e) => (
                        <tr
                          key={e.id}
                          className="border-t border-border/70 cursor-pointer hover:bg-muted/40 transition-colors"
                          onClick={() => { setExpenseEdit(e); setShowExpense(true); }}
                          data-testid="expense-row"
                        >
                          <td className="py-2 text-muted-foreground whitespace-nowrap">
                            <span className="md:hidden">{formatDateShortBR(e.date)}</span>
                            <span className="hidden md:inline">{formatDateBR(e.date)}</span>
                          </td>
                          <td className="py-2 hidden md:table-cell">{EXPENSE_CATEGORIES[e.category] || e.category}</td>
                          <td className="py-2">{e.description}</td>
                          <td className="py-2 text-muted-foreground hidden md:table-cell">
                            {e.paid_by_type === 'COMPANY' ? 'Empresa' : (e.paid_by_partner_name || 'Sócio')}
                          </td>
                          <td className="py-2 text-right tabular font-medium whitespace-nowrap">{formatBRL(e.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="md:hidden border-t-2 border-border">
                        <td colSpan={2} className="py-3 font-medium text-right">Total</td>
                        <td className="py-3 text-right font-semibold tabular whitespace-nowrap">{formatBRL(calc.total_expenses)}</td>
                      </tr>
                      <tr className="hidden md:table-row border-t-2 border-border">
                        <td colSpan={4} className="py-3 font-medium text-right">Total</td>
                        <td className="py-3 text-right font-semibold tabular whitespace-nowrap">{formatBRL(calc.total_expenses)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {isSold && (
          <TabsContent value="pos-venda" className="mt-4">
            <Card className="border-border/70">
              <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600" /> 
                  Despesas Pós-Venda
                </CardTitle>
                <Button 
                  size="sm" 
                  onClick={() => setShowPostSaleExpense(true)} 
                  className="bg-amber-600 hover:bg-amber-700 text-white" 
                  data-testid="add-post-sale-expense"
                >
                  <Plus className="h-4 w-4 mr-1" /> Registrar Despesa
                </Button>
              </CardHeader>
              <CardContent className="px-3 md:px-6">
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs mb-4">
                  <div className="font-medium text-amber-900 mb-1">ℹ️ Sobre Despesas Pós-Venda</div>
                  <div className="text-amber-800">
                    Registre aqui despesas de garantia, consertos ou problemas identificados após a venda. 
                    <strong> O valor será distribuído automaticamente entre os sócios</strong> proporcionalmente à participação deles na venda deste veículo.
                  </div>
                </div>

                {postSaleExpenses.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    Nenhuma despesa pós-venda registrada.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs md:text-sm">
                      <thead className="text-muted-foreground">
                        <tr>
                          <th className="text-left py-2 font-medium">Data</th>
                          <th className="text-left py-2 font-medium">Descrição</th>
                          <th className="text-right py-2 font-medium">Valor</th>
                          <th className="text-center py-2 font-medium w-20">Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {postSaleExpenses.map((exp) => (
                          <tr key={exp.id} className="border-t border-border/70" data-testid="post-sale-expense-row">
                            <td className="py-3 text-muted-foreground whitespace-nowrap">
                              <span className="md:hidden">{formatDateShortBR(exp.expense_date)}</span>
                              <span className="hidden md:inline">{formatDateBR(exp.expense_date)}</span>
                            </td>
                            <td className="py-3">{exp.description}</td>
                            <td className="py-3 text-right tabular font-medium text-red-600 whitespace-nowrap">
                              {formatBRL(exp.amount)}
                            </td>
                            <td className="py-3 text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeletePostSaleExpense(exp.id)}
                                data-testid="delete-post-sale-expense"
                              >
                                <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-border">
                          <td colSpan={2} className="py-3 font-medium text-right">Total</td>
                          <td className="py-3 text-right font-semibold tabular text-red-600 whitespace-nowrap">
                            {formatBRL(postSaleExpenses.reduce((sum, exp) => sum + exp.amount, 0))}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        <TabsContent value="dre" className="mt-4">
          <DreCard calc={calc} vehicleSold={isSold} vehicle={v} expenses={data.expenses} />
        </TabsContent>

        <TabsContent value="checklist" className="mt-4">
          <ChecklistTab vehicleId={id} />
        </TabsContent>
      </Tabs>

      <ExpenseDialog
        open={showExpense}
        onOpenChange={setShowExpense}
        vehicleId={id}
        partners={data.partners}
        edit={expenseEdit}
        onSaved={load}
      />
      <PostSaleExpenseDialogInline
        open={showPostSaleExpense}
        onOpenChange={setShowPostSaleExpense}
        vehicleId={id}
        onSaved={load}
      />
      <SellDialog open={showSell} onOpenChange={setShowSell} vehicleId={id} reservation={v} partners={data.partners} expenses={data.expenses} onSold={load} />
      <SellDialog
        open={showEditSale}
        onOpenChange={setShowEditSale}
        vehicleId={id}
        reservation={v}
        partners={data.partners}
        expenses={data.expenses}
        onSold={load}
        editMode={true}
      />
      <ReserveDialog open={showReserve} onOpenChange={setShowReserve} vehicleId={id} reservation={v} onReserved={load} />
      <CancelReservationDialog
        open={showCancelReservation}
        onOpenChange={setShowCancelReservation}
        vehicleId={id}
        reservation={v}
        onCancelled={load}
      />
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent>
          <DialogHeader><DialogTitle>Excluir veículo?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Esta ação também exclui as despesas vinculadas. Não pode ser desfeita.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="vehicle-delete-confirm">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showUndoSale} onOpenChange={setShowUndoSale}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" /> Desfazer venda?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Mudar o status para <span className="font-medium text-foreground">"{VEHICLE_STATUS[undoTargetStatus]?.label || undoTargetStatus}"</span> irá <strong>apagar todos os dados desta venda</strong>:
            </p>
            <div className="rounded-lg border border-border/70 bg-muted/30 p-3 space-y-1.5 text-sm">
              {v.sale_price && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Valor da venda</span>
                  <span className="tabular font-medium">{formatBRL(v.sale_price)}</span>
                </div>
              )}
              {v.sale_date && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Data da venda</span>
                  <span className="tabular">{formatDateBR(v.sale_date)}</span>
                </div>
              )}
              {v.buyer_name && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Comprador</span>
                  <span>{v.buyer_name}</span>
                </div>
              )}
              {v.sale_payments && v.sale_payments.length > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Formas de pagamento</span>
                  <span>{v.sale_payments.length} cadastrada(s)</span>
                </div>
              )}
            </div>

            {linkedTradeVehicles.length > 0 && (
              <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-3 space-y-3" data-testid="trade-decision-block">
                <div className="flex items-start gap-2">
                  <Car className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-amber-900">
                      O que fazer com o{linkedTradeVehicles.length > 1 ? 's' : ''} veículo{linkedTradeVehicles.length > 1 ? 's' : ''} recebido{linkedTradeVehicles.length > 1 ? 's' : ''} em troca?
                    </div>
                    <div className="text-xs text-amber-800 mt-0.5">Esta venda criou {linkedTradeVehicles.length} veículo{linkedTradeVehicles.length > 1 ? 's' : ''} no estoque:</div>
                  </div>
                </div>
                <div className="space-y-1 pl-6">
                  {linkedTradeVehicles.map((tv) => (
                    <div key={tv.id} className="text-xs text-amber-900 flex items-center gap-1">
                      <Car className="h-3 w-3" />
                      <span>{tv.brand} {tv.model}{tv.version ? ' ' + tv.version : ''} · Placa {tv.plate || '—'}</span>
                      {tv.status === 'SOLD' && (
                        <span className="text-[10px] bg-red-100 text-red-900 px-1.5 py-0.5 rounded ml-1">já vendido</span>
                      )}
                    </div>
                  ))}
                </div>
                <div className="space-y-2 pt-1 border-t border-amber-200">
                  <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="undo-trade-delete">
                    <input
                      type="radio"
                      name="trade-decision"
                      checked={!keepTrades}
                      onChange={() => setKeepTrades(false)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-[hsl(var(--danger))]">Excluir o(s) veículo(s) de troca</div>
                      <div className="text-xs text-muted-foreground">Remove do estoque junto com despesas. Use quando a venda inteira é um cancelamento.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="undo-trade-keep">
                    <input
                      type="radio"
                      name="trade-decision"
                      checked={keepTrades}
                      onChange={() => setKeepTrades(true)}
                      className="mt-1"
                    />
                    <div>
                      <div className="font-medium text-amber-900">Manter no estoque</div>
                      <div className="text-xs text-muted-foreground">O(s) veículo(s) permanece(m) no estoque, desvinculado(s) desta venda. Use quando você só quer reabrir a venda.</div>
                    </div>
                  </label>
                </div>
              </div>
            )}

            <p className="text-xs text-muted-foreground">⚠️ Os dados da venda (valores, formas de pagamento, comprador, créditos dos sócios) serão apagados.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUndoSale(false)}>Cancelar</Button>
            <Button
              variant={keepTrades && linkedTradeVehicles.length > 0 ? 'default' : 'destructive'}
              onClick={confirmUndoSale}
              data-testid="undo-sale-confirm"
              className={keepTrades && linkedTradeVehicles.length > 0 ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
            >
              {linkedTradeVehicles.length > 0 && keepTrades
                ? 'Desfazer venda · Manter troca no estoque'
                : (linkedTradeVehicles.length > 0
                    ? 'Desfazer venda · Excluir troca'
                    : 'Confirmar e apagar dados da venda')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Item({ label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}

function Row({ label, value, bold, intent, muted }) {
  const intentClass = intent === 'success' ? 'text-[hsl(var(--success))]' : intent === 'danger' ? 'text-[hsl(var(--danger))]' : '';
  const mutedClass = muted ? 'text-muted-foreground' : '';
  return (
    <div className="flex items-center justify-between">
      <div className="text-muted-foreground">{label}</div>
      <div className={`tabular ${bold ? 'font-semibold' : ''} ${intentClass} ${mutedClass}`}>{value}</div>
    </div>
  );
}

function DreCard({ calc, vehicleSold, vehicle, expenses }) {
  const reservationDeposit = vehicle?.reservation_deposit || 0;
  const showReservationLine = reservationDeposit > 0;
  const expensesList = expenses || [];
  const hasTrade = (calc.trade_received || 0) > 0;
  // Calculate realized vs forecast for sale payments
  const todayStr = todayISO();
  const payments = vehicle?.sale_payments || [];
  const cashPayments = payments.filter((p) => p.method !== 'TRADE');
  const realizedCash = cashPayments.filter((p) => !p.date || p.date <= todayStr).reduce((s, p) => s + (p.amount || 0), 0);
  const forecastCash = cashPayments.filter((p) => p.date && p.date > todayStr).reduce((s, p) => s + (p.amount || 0), 0);
  const hasForecastPayments = forecastCash > 0;
  return (
    <Card className="border-border/70">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Calculator className="h-4 w-4 text-muted-foreground" /> DRE — Demonstração do Resultado
        </CardTitle>
        <p className="text-xs text-muted-foreground">Extrato detalhado da operação financeira.</p>
      </CardHeader>
      <CardContent>
        {calc.is_projection && (
          <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3 flex items-start gap-2" data-testid="dre-projection-alert">
            <Info className="h-4 w-4 text-blue-700 mt-0.5 shrink-0" />
            <div className="flex-1 text-xs">
              <div className="font-medium text-blue-900">⚠️ Projeção baseada em valor mínimo</div>
              <div className="text-blue-800 mt-0.5">
                Veículo não vendido. Resultado calculado com base no valor mínimo de venda: <span className="font-semibold tabular">{formatBRL(calc.minimum_sale_price)}</span>
              </div>
            </div>
          </div>
        )}
        {hasForecastPayments && vehicleSold && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="dre-forecast-alert">
            <Hourglass className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1 text-xs">
              <div className="font-medium text-amber-900">Esta venda tem recebimento parcelado</div>
              <div className="text-amber-800 mt-0.5">
                Já entrou no caixa: <span className="font-semibold tabular">{formatBRL(realizedCash)}</span>
                {' · '}
                A receber em datas futuras: <span className="font-semibold tabular">{formatBRL(forecastCash)}</span>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-2" data-testid="dre-card">

          {/* RECEITA */}
          <div>
            <div className="flex items-center justify-between py-2 border-b border-border/70">
              <span className="text-sm font-semibold flex items-center gap-2">
                <span className="text-[hsl(var(--success))] text-base">=</span>
                <span>Valor da venda</span>
              </span>
              <span className="text-sm font-semibold tabular">{calc.sale_price !== null ? formatBRL(calc.sale_price) : '— (não vendido)'}</span>
            </div>
            {vehicle?.sale_payments && vehicle.sale_payments.length > 0 && (
              <div className="py-2 pl-6 space-y-1">
                {sortSalePayments(vehicle.sale_payments).map((p, i) => {
                  const isFuture = p.date && p.date > todayISO();
                  return (
                    <div key={i} className={`flex items-center justify-between text-xs ${isFuture ? 'text-amber-800' : 'text-muted-foreground'}`}>
                      <span className="flex items-center gap-1.5 flex-wrap">
                        <span>{PAYMENT_METHOD_ICONS[p.method] || '➖'}</span>
                        <span>{PAYMENT_METHODS[p.method] || p.method}</span>
                        {p.date && (
                          <span className={`text-[10px] tabular ${isFuture ? 'font-medium' : ''}`}>
                            · {formatDateBR(p.date)}
                          </span>
                        )}
                        {isFuture && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-100 text-amber-800 border border-amber-200">
                            <Hourglass className="h-2.5 w-2.5" /> Previsto
                          </span>
                        )}
                        {p.method === 'TRADE' && p.trade_vehicle_label && (
                          <span className="text-[10px]">({p.trade_vehicle_label})</span>
                        )}
                      </span>
                      <span className={`tabular ${isFuture ? 'italic' : ''}`}>{formatBRL(p.amount)}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sinal retido (se aplicável) */}
          {calc.retained_reservation_deposit > 0 && (
            <div className="flex items-center justify-between py-2 border-b border-border/70">
              <span className="text-sm font-semibold flex items-center gap-2">
                <span className="text-[hsl(var(--success))] text-base">+</span>
                <Bookmark className="h-3.5 w-3.5 text-emerald-700" />
                <span>Sinal retido (reserva cancelada)</span>
              </span>
              <span className="text-sm font-semibold tabular text-[hsl(var(--success))]">+{formatBRL(calc.retained_reservation_deposit)}</span>
            </div>
          )}

          {/* CUSTO DE AQUISIÇÃO */}
          <div className="flex items-center justify-between py-2 border-b border-border/70">
            <span className="text-sm font-semibold flex items-center gap-2">
              <span className="text-[hsl(var(--danger))] text-base">−</span>
              <span>Custo de aquisição</span>
            </span>
            <span className="text-sm font-semibold tabular text-[hsl(var(--danger))]">{formatBRL(calc.purchase_price)}</span>
          </div>

          {/* DESPESAS */}
          <div>
            <div className="flex items-center justify-between py-2 border-b border-border/70">
              <span className="text-sm font-semibold flex items-center gap-2">
                <span className="text-[hsl(var(--danger))] text-base">−</span>
                <Receipt className="h-3.5 w-3.5 text-muted-foreground" />
                <span>Despesas{expensesList.length > 0 ? ` (${expensesList.length})` : ''}</span>
              </span>
              <span className="text-sm font-semibold tabular text-[hsl(var(--danger))]">{formatBRL(calc.total_expenses)}</span>
            </div>
            {expensesList.length > 0 && (
              <div className="py-2 pl-6 space-y-1">
                {expensesList.map((e) => (
                  <div key={e.id} className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0 pr-2">
                      <span className="tabular text-muted-foreground/70 shrink-0">{formatDateBR(e.date)}</span>
                      <span className="text-muted-foreground/60">·</span>
                      <span className="text-[10px] uppercase tracking-wide bg-muted px-1.5 py-0.5 rounded shrink-0">{EXPENSE_CATEGORIES[e.category] || e.category}</span>
                      <span className="truncate">{e.description}</span>
                    </div>
                    <span className="tabular shrink-0">{formatBRL(e.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            {expensesList.length === 0 && (
              <div className="py-1 pl-6 text-xs text-muted-foreground italic">Nenhuma despesa registrada</div>
            )}
          </div>

          {/* IMPOSTOS (se aplicável) */}
          {(vehicleSold || calc.is_projection) && calc.taxes && (
            <div>
              <div className="flex items-center justify-between py-2 border-b border-border/70">
                <span className="text-sm font-semibold flex items-center gap-2">
                  <span className="text-[hsl(var(--danger))] text-base">−</span>
                  <Calculator className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>Impostos sobre a operação</span>
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          tabIndex={0}
                          role="button"
                          aria-label="Informação sobre adicional IRPJ trimestral"
                          className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-muted-foreground/10 text-muted-foreground hover:bg-muted-foreground/20 focus:outline-none focus:ring-1 focus:ring-[hsl(var(--ring))]"
                          style={{ cursor: 'default' }}
                        >
                          <Info className="h-2.5 w-2.5" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs text-xs">
                        <div className="space-y-1.5">
                          <div>Margem fiscal: <strong>{formatBRL(calc.taxes.margem_fiscal)}</strong></div>
                          {calc.taxes.has_subdeclaration && (
                            <div className="text-[10px] text-amber-200 italic">
                              Calculada sobre o valor do documento ({formatBRL(calc.taxes.tax_basis)}), inferior ao valor real da venda ({formatBRL(calc.sale_price)}).
                            </div>
                          )}
                          <div className="pt-1.5 border-t border-border/40">
                            <div className="font-semibold mb-0.5">Adicional IRPJ trimestral</div>
                            <div>Alíquota de <strong>10%</strong> incide sobre o que exceder R$ 60.000 de lucro presumido acumulado no trimestre — apurado no fechamento.</div>
                            <div className="mt-1">Lucro presumido desta operação: <strong>{formatBRL(calc.taxes.lucro_presumido_operacao)}</strong></div>
                          </div>
                        </div>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </span>
                <span className="text-sm font-semibold tabular text-[hsl(var(--danger))]">{formatBRL(calc.taxes.total_taxes)}</span>
              </div>
              <div className="py-2 pl-6 space-y-1">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>PIS <span className="text-[10px] text-muted-foreground/70">({calc.taxes.pis_rate_pct}% × margem)</span></span>
                  <span className="tabular">{formatBRL(calc.taxes.pis)}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>COFINS <span className="text-[10px] text-muted-foreground/70">({calc.taxes.cofins_rate_pct}% × margem)</span></span>
                  <span className="tabular">{formatBRL(calc.taxes.cofins)}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>IRPJ <span className="text-[10px] text-muted-foreground/70">({calc.taxes.irpj_rate_pct}% efetivo)</span></span>
                  <span className="tabular">{formatBRL(calc.taxes.irpj)}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>CSLL <span className="text-[10px] text-muted-foreground/70">({calc.taxes.csll_rate_pct}% efetivo)</span></span>
                  <span className="tabular">{formatBRL(calc.taxes.csll)}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    ICMS a recolher <span className="text-[10px] text-muted-foreground/70">(base {calc.taxes.icms_base_pct}% × {calc.taxes.icms_rate_pct}%)</span>
                    {calc.taxes.icms_credit_entry > 0 && (
                      <span className="text-[10px] text-muted-foreground ml-1">(-{formatBRL(calc.taxes.icms_credit_entry)} crédito)</span>
                    )}
                  </span>
                  <span className="tabular">{formatBRL(calc.taxes.icms_a_recolher)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Adiantamento em caixa (se reservado e não vendido) */}
          {!vehicleSold && showReservationLine && (
            <div className="flex items-center justify-between py-3 px-4 rounded-xl my-2 border bg-amber-50 border-amber-200">
              <span className="text-sm font-medium text-amber-800 flex items-center gap-2">
                <Bookmark className="h-3.5 w-3.5" /> Adiantamento em caixa (sinal de reserva)
              </span>
              <span className="text-base font-semibold tabular text-amber-800">+{formatBRL(reservationDeposit)}</span>
            </div>
          )}

          {/* RESULTADO LÍQUIDO */}
          {(vehicleSold || calc.is_projection) && (
            <div className={`flex items-center justify-between py-3 px-4 rounded-xl mt-3 border ${calc.net_result >= 0 ? 'bg-[hsl(var(--success-bg))] border-emerald-200' : 'bg-[hsl(var(--danger-bg))] border-red-200'}`}>
              <span className={`text-sm font-semibold flex items-center gap-2 ${calc.net_result >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]'}`}>
                <span className="text-base">=</span>
                <span>{calc.net_result >= 0 ? 'Lucro' : 'Prejuízo'} líquido{calc.taxes ? ' (após impostos)' : ''}</span>
              </span>
              <span className={`text-lg font-bold tabular ${calc.net_result >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]'}`} data-testid="dre-net-profit-value">
                {formatBRL(calc.net_result, { sign: true })}
              </span>
            </div>
          )}
        </div>

        {/* DISTRIBUIÇÃO POR SÓCIO */}
        {vehicleSold && calc.per_partner.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" /> Distribuição por sócio
            </h3>
            {hasTrade && (
              <p className="text-xs text-muted-foreground mb-3">
                ⓘ Como parte do recebimento foi em troca, o valor <strong>distribuível em dinheiro</strong> é proporcional ao cash recebido. O restante está representado em ativo.
              </p>
            )}
            <div className="overflow-x-auto hidden md:block" data-testid="dre-partner-distribution">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border/70">
                    <th className="text-left py-2 pr-3 font-medium">Sócio</th>
                    <th className="text-center py-2 px-3 font-medium">Aporte</th>
                    <th className="text-center py-2 px-3 font-medium">Reembolso</th>
                    <th className="text-center py-2 px-3 font-medium">{calc.per_partner[0]?.profit_share >= 0 ? 'Lucro' : 'Resultado'}</th>
                    <th className="text-center py-2 px-3 font-medium">Total contábil</th>
                    {hasTrade && <th className="text-center py-2 px-3 font-medium bg-[hsl(var(--success-bg))] text-[hsl(var(--success))] rounded-tl-md">💰 Dinheiro</th>}
                    {hasTrade && <th className="text-center py-2 pl-3 font-medium bg-amber-100 text-amber-900 rounded-tr-md">🚗 Troca</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/70">
                  {calc.per_partner.map((p) => (
                    <tr key={p.partner_id} className="hover:bg-muted/30">
                      <td className="py-3 pr-3">
                        <div className="font-medium">{p.partner_name}</div>
                        <div className="text-[11px] text-muted-foreground tabular">{formatPercent(p.contribution_pct)} aporte · {formatPercent(p.profit_pct)} resultado</div>
                      </td>
                      <td className="py-3 px-3 text-center tabular">{formatBRL(p.initial_contribution)}</td>
                      <td className="py-3 px-3 text-center tabular">{formatBRL(p.expenses_paid)}</td>
                      <td className={`py-3 px-3 text-center tabular font-medium ${p.profit_share >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]'}`}>{formatBRL(p.profit_share, { sign: true })}</td>
                      <td className="py-3 px-3 text-center tabular font-semibold">{formatBRL(p.total_to_receive)}</td>
                      {hasTrade && (
                        <td className="py-3 px-3 text-center tabular font-semibold bg-[hsl(var(--success-bg))]/40 text-[hsl(var(--success))]">
                          {p.cash_to_receive !== null && p.cash_to_receive !== undefined ? formatBRL(p.cash_to_receive) : '—'}
                        </td>
                      )}
                      {hasTrade && (
                        <td className="py-3 pl-3 text-center tabular font-semibold bg-amber-50 text-amber-900">
                          {p.asset_to_receive !== null && p.asset_to_receive !== undefined ? formatBRL(p.asset_to_receive) : '—'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: cards stacked */}
            <div className="md:hidden space-y-3" data-testid="dre-partner-distribution-mobile">
              {calc.per_partner.map((p) => (
                <div key={p.partner_id} className="border border-border/70 rounded-xl p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-sm">{p.partner_name}</div>
                    <div className="text-[10px] text-muted-foreground tabular text-right">{formatPercent(p.contribution_pct)} apt · {formatPercent(p.profit_pct)} res</div>
                  </div>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Aporte</span>
                      <span className="tabular">{formatBRL(p.initial_contribution)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Reembolso</span>
                      <span className="tabular">{formatBRL(p.expenses_paid)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{p.profit_share >= 0 ? 'Lucro' : 'Resultado'}</span>
                      <span className={`tabular font-medium ${p.profit_share >= 0 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--danger))]'}`}>{formatBRL(p.profit_share, { sign: true })}</span>
                    </div>
                    <div className="flex justify-between pt-1 border-t border-border/70 font-semibold">
                      <span>Total contábil</span>
                      <span className="tabular">{formatBRL(p.total_to_receive)}</span>
                    </div>
                  </div>
                  {hasTrade && (
                    <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                      <div className="rounded bg-[hsl(var(--success-bg))]/40 p-2">
                        <div className="text-[10px] text-[hsl(var(--success))]">💰 Dinheiro</div>
                        <div className="tabular font-semibold text-[hsl(var(--success))]">
                          {p.cash_to_receive !== null && p.cash_to_receive !== undefined ? formatBRL(p.cash_to_receive) : '—'}
                        </div>
                      </div>
                      <div className="rounded bg-amber-50 p-2">
                        <div className="text-[10px] text-amber-900">🚗 Troca</div>
                        <div className="tabular font-semibold text-amber-900">
                          {p.asset_to_receive !== null && p.asset_to_receive !== undefined ? formatBRL(p.asset_to_receive) : '—'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ExpenseDialog({ open, onOpenChange, vehicleId, partners, edit, onSaved }) {
  const [form, setForm] = useState({
    category: 'PREPARATION',
    description: '',
    amount: '',
    date: todayISO(),
    paid_by_type: 'COMPANY',
    paid_by_partner_id: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (edit) {
      setForm({
        category: edit.category,
        description: edit.description,
        amount: edit.amount,
        date: edit.date,
        paid_by_type: edit.paid_by_type,
        paid_by_partner_id: edit.paid_by_partner_id || '',
      });
    } else {
      setForm({
        category: 'PREPARATION',
        description: '',
        amount: '',
        date: todayISO(),
        paid_by_type: 'COMPANY',
        paid_by_partner_id: '',
      });
    }
  }, [edit, open]);

  const submit = async () => {
    if (!form.description || !form.amount || Number(form.amount) <= 0) {
      toast.error('Descrição e valor são obrigatórios');
      return;
    }
    if (form.paid_by_type === 'PARTNER' && !form.paid_by_partner_id) {
      toast.error('Selecione o sócio pagador');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        paid_by_partner_id: form.paid_by_type === 'PARTNER' ? form.paid_by_partner_id : null,
      };
      if (edit) {
        await api.put(`/expenses/${edit.id}`, payload);
      } else {
        await api.post('/expenses', { ...payload, vehicle_id: vehicleId });
      }
      toast.success(edit ? 'Despesa atualizada' : 'Despesa adicionada');
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{edit ? 'Editar despesa' : 'Nova despesa'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="expense-category-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(EXPENSE_CATEGORIES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} data-testid="expense-date-input" />
            </div>
          </div>
          <div>
            <Label>Descrição</Label>
            <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Ex: Funilaria + pintura" data-testid="expense-description-input" />
          </div>
          <div>
            <Label>Valor</Label>
            <CurrencyInput value={form.amount} onChange={(v) => setForm((f) => ({ ...f, amount: v }))} data-testid="expense-amount-input" />
          </div>
          <div>
            <Label>Pago por</Label>
            <Select value={form.paid_by_type} onValueChange={(v) => setForm((f) => ({ ...f, paid_by_type: v }))}>
              <SelectTrigger data-testid="expense-paidby-input"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="COMPANY">Empresa (caixa comum)</SelectItem>
                <SelectItem value="PARTNER">Sócio específico</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.paid_by_type === 'PARTNER' && (
            <div>
              <Label>Sócio pagador</Label>
              <Select value={form.paid_by_partner_id} onValueChange={(v) => setForm((f) => ({ ...f, paid_by_partner_id: v }))}>
                <SelectTrigger data-testid="expense-partner-input"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {partners.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter className="flex-row sm:justify-between gap-2">
            {edit ? (
              <Button
                variant="outline"
                onClick={async () => {
                  if (!window.confirm('Excluir esta despesa?')) return;
                  try {
                    await api.delete(`/expenses/${edit.id}`);
                    toast.success('Despesa excluída');
                    onOpenChange(false);
                    onSaved();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Erro ao excluir');
                  }
                }}
                className="text-[hsl(var(--danger))] border-[hsl(var(--danger))]/30 hover:bg-[hsl(var(--danger))]/10 hover:text-[hsl(var(--danger))]"
                data-testid="expense-dialog-delete"
              >
                <Trash2 className="h-4 w-4 mr-1.5" /> Excluir
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button onClick={submit} disabled={submitting} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="expense-save-button">
                {submitting ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SellDialog({ open, onOpenChange, vehicleId, reservation, partners, expenses, onSold, editMode = false }) {
  const [form, setForm] = useState({
    sale_price: '',
    sale_date: todayISO(),
    same_as_document: true,
    document_sale_price: '',
    buyer_name: '',
    buyer_document: '',
    buyer_phone: '',
    buyer_email: '',
    buyer_info: '',
    buyer_person_id: '',
  });
  const [payments, setPayments] = useState([]); // [{tempId, method, amount, notes, trade_vehicle?}]
  const [submitting, setSubmitting] = useState(false);
  const [tradeDialogOpen, setTradeDialogOpen] = useState(false);
  const [showBuyerPicker, setShowBuyerPicker] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentDialogMethod, setPaymentDialogMethod] = useState('PIX');
  const [editingPaymentIdx, setEditingPaymentIdx] = useState(null);
  const [editingTradeIdx, setEditingTradeIdx] = useState(null);

  useEffect(() => {
    if (open) {
      if (editMode && reservation) {
        // Pre-fill with existing sale data
        const v = reservation;
        const docSameAsReal = !v.document_sale_price || Math.abs((v.document_sale_price || 0) - (v.sale_price || 0)) < 0.01;
        setForm({
          sale_price: v.sale_price ?? '',
          sale_date: v.sale_date || todayISO(),
          same_as_document: docSameAsReal,
          document_sale_price: docSameAsReal ? '' : (v.document_sale_price ?? ''),
          buyer_name: v.buyer_name || '',
          buyer_document: v.buyer_document || '',
          buyer_phone: v.buyer_phone || '',
          buyer_email: v.buyer_email || '',
          buyer_info: v.buyer_info || '',
          buyer_person_id: v.buyer_person_id || '',
          sale_notes: v.sale_notes || '',
          address_cep: v.buyer_address_cep || '',
          address_street: v.buyer_address_street || '',
          address_number: v.buyer_address_number || '',
          address_neighborhood: v.buyer_address_neighborhood || '',
          address_city: v.buyer_address_city || '',
          address_state: v.buyer_address_state || '',
          address_complement: v.buyer_address_complement || '',
        });
        const existingPayments = (v.sale_payments || []).map((p, idx) => ({
          tempId: `existing_${idx}_${Date.now()}`,
          method: p.method,
          amount: p.amount,
          notes: p.notes || '',
          date: p.date || v.sale_date || todayISO(),
          trade_vehicle_id: p.trade_vehicle_id || null,
          trade_vehicle_label: p.trade_vehicle_label || null,
          // Backend now expands trade_vehicle (brand, model, plate, allocations, etc) for TRADE payments
          trade_vehicle: p.method === 'TRADE' ? (p.trade_vehicle || null) : null,
          _locked: false,
        }));
        setPayments(existingPayments);
      } else {
        setForm({
          sale_price: '',
          sale_date: todayISO(),
          same_as_document: true,
          document_sale_price: '',
          buyer_name: reservation?.reservation_buyer_name || '',
          buyer_document: reservation?.reservation_buyer_document || '',
          buyer_phone: reservation?.reservation_buyer_phone || '',
          buyer_email: reservation?.reservation_buyer_email || '',
          buyer_info: '',
          buyer_person_id: '',
          sale_notes: '',
          address_cep: '',
          address_street: '',
          address_number: '',
          address_neighborhood: '',
          address_city: '',
          address_state: '',
          address_complement: '',
        });
        // Initialize with reservation deposit as PIX (most common) if exists
        if (reservation?.reservation_deposit > 0) {
          setPayments([{
            tempId: Date.now(),
            method: 'PIX',
            amount: reservation.reservation_deposit,
            notes: 'Sinal de reserva (já recebido)',
            date: reservation?.reservation_deposit_date || todayISO(),
          }]);
        } else {
          setPayments([]);
        }
      }
      setEditingTradeIdx(null);
    }
  }, [open, reservation, editMode]);

  const totalPayments = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const remaining = Number(form.sale_price || 0) - totalPayments;
  const paymentsValid = payments.length === 0 || Math.abs(remaining) < 0.01;

  const addPayment = (method) => {
    if (method === 'TRADE') {
      setEditingTradeIdx(null);
      setTradeDialogOpen(true);
      return;
    }
    // Open payment modal with the selected method
    setEditingPaymentIdx(null);
    setPaymentDialogMethod(method);
    setPaymentDialogOpen(true);
  };

  const onPaymentConfirm = (paymentData) => {
    if (editingPaymentIdx !== null) {
      setPayments((ps) => ps.map((p, i) => (i === editingPaymentIdx ? { ...p, ...paymentData, tempId: p.tempId } : p)));
    } else {
      setPayments((ps) => [...ps, { tempId: Date.now() + Math.random(), ...paymentData }]);
    }
    setPaymentDialogOpen(false);
    setEditingPaymentIdx(null);
  };

  const updatePayment = (idx, field, value) => {
    setPayments((ps) => ps.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };
  const removePayment = (idx) => setPayments((ps) => ps.filter((_, i) => i !== idx));

  const onTradeConfirm = (paymentData) => {
    if (editingTradeIdx !== null) {
      setPayments((ps) => ps.map((p, i) => (i === editingTradeIdx ? { ...p, ...paymentData, tempId: p.tempId } : p)));
    } else {
      setPayments((ps) => [...ps, { tempId: Date.now() + Math.random(), ...paymentData }]);
    }
    setTradeDialogOpen(false);
    setEditingTradeIdx(null);
  };

  const submit = async () => {
    if (!form.sale_price || Number(form.sale_price) <= 0) {
      toast.error('Preço de venda inválido');
      return;
    }
    if (!form.same_as_document && (!form.document_sale_price || Number(form.document_sale_price) <= 0)) {
      toast.error('Informe o valor que constará no documento');
      return;
    }
    if (payments.length > 0 && !paymentsValid) {
      toast.error(`A soma das formas de pagamento deve ser igual ao valor da venda. Diferença: ${formatBRL(Math.abs(remaining))}`);
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        sale_price: Number(form.sale_price),
        sale_date: form.sale_date,
        document_sale_price: form.same_as_document ? Number(form.sale_price) : Number(form.document_sale_price),
        buyer_name: form.buyer_name.trim() || null,
        buyer_document: form.buyer_document || null,
        buyer_phone: form.buyer_phone || null,
        buyer_email: form.buyer_email.trim() || null,
        buyer_info: form.buyer_info.trim() || null,
        buyer_person_id: form.buyer_person_id || null,
        sale_notes: form.sale_notes.trim() || null,
        buyer_address_cep: form.address_cep.trim() || null,
        buyer_address_street: form.address_street.trim() || null,
        buyer_address_number: form.address_number.trim() || null,
        buyer_address_neighborhood: form.address_neighborhood.trim() || null,
        buyer_address_city: form.address_city.trim() || null,
        buyer_address_state: form.address_state.trim() || null,
        buyer_address_complement: form.address_complement.trim() || null,
      };
      if (payments.length > 0) {
        body.payments = payments.map((p) => ({
          method: p.method,
          amount: Number(p.amount),
          notes: p.notes || null,
          date: p.date || form.sale_date,
          trade_vehicle: p.trade_vehicle || null,
        }));
      }
      if (editMode) {
        await api.put(`/vehicles/${vehicleId}/sale-edit`, body);
        toast.success('Venda atualizada.');
      } else {
        await api.post(`/vehicles/${vehicleId}/sell`, body);
        toast.success('Venda registrada! Resultado calculado.');
      }
      onOpenChange(false);
      onSold();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao registrar venda');
    } finally {
      setSubmitting(false);
    }
  };

  const editingTrade = editingTradeIdx !== null ? payments[editingTradeIdx] : null;
  const sellerForDialog = { allocations: reservation?.allocations || [] };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editMode ? 'Editar venda' : 'Registrar venda'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {reservation?.reservation_deposit > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm flex items-start gap-2">
                <Bookmark className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                <div>
                  <div className="font-medium text-amber-900">Veículo reservado{reservation.reservation_buyer_name ? ` por ${reservation.reservation_buyer_name}` : ''}</div>
                  <div className="text-xs text-amber-800 mt-0.5">Sinal já recebido: <span className="font-semibold tabular">{formatBRL(reservation.reservation_deposit)}</span>. Já adicionado às formas de pagamento abaixo.</div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data da venda</Label>
                <Input type="date" value={form.sale_date} onChange={(e) => setForm((f) => ({ ...f, sale_date: e.target.value }))} data-testid="sell-date-input" />
              </div>
              <div>
                <Label>Valor da venda (real) *</Label>
                <CurrencyInput value={form.sale_price} onChange={(v) => setForm((f) => ({ ...f, sale_price: v }))} data-testid="sell-price-input" />
              </div>
            </div>

            <div className="rounded-lg border border-border/70 bg-muted/30 p-3 space-y-3">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="sell-same-document">
                <input
                  type="checkbox"
                  checked={form.same_as_document}
                  onChange={(e) => setForm((f) => ({ ...f, same_as_document: e.target.checked, document_sale_price: e.target.checked ? '' : f.document_sale_price }))}
                  className="mt-0.5 h-4 w-4 rounded border-border accent-[hsl(var(--primary))]"
                />
                <div className="text-sm">
                  <div className="font-medium">O valor do documento é o mesmo da venda</div>
                  <div className="text-xs text-muted-foreground">Desmarque se o valor declarado em documento for diferente do real.</div>
                </div>
              </label>
              {!form.same_as_document && (
                <div>
                  <Label>Valor no documento *</Label>
                  <CurrencyInput value={form.document_sale_price} onChange={(v) => setForm((f) => ({ ...f, document_sale_price: v }))} data-testid="sell-document-price-input" />
                </div>
              )}
            </div>

            <Separator />

            {/* Forma de pagamento */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Forma de pagamento</Label>
                <div className="text-xs text-muted-foreground">Como o valor de {formatBRL(form.sale_price || 0)} foi recebido</div>
              </div>

              {payments.length === 0 ? (
                <div className="text-sm text-muted-foreground border border-dashed border-border rounded-lg p-3 text-center">
                  Nenhuma forma de pagamento adicionada. Adicione abaixo se quiser detalhar (Pix, Troca, etc.) ou deixe em branco para registrar venda simples.
                </div>
              ) : (
                <div className="space-y-2">
                  {payments
                    .map((p, idx) => ({ p, idx }))
                    .sort((a, b) => {
                      // TROCA first
                      const aTrade = a.p.method === 'TRADE' ? 0 : 1;
                      const bTrade = b.p.method === 'TRADE' ? 0 : 1;
                      if (aTrade !== bTrade) return aTrade - bTrade;
                      // Then by date ascending (null/empty dates go last)
                      const ad = a.p.date || '9999-12-31';
                      const bd = b.p.date || '9999-12-31';
                      return ad.localeCompare(bd);
                    })
                    .map(({ p, idx }) => (
                    <div key={p.tempId} className="border border-border/70 rounded-lg p-3 bg-card flex items-center gap-3" data-testid="payment-row">
                      <div className="text-2xl">{PAYMENT_METHOD_ICONS[p.method] || '➖'}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                          <span>{PAYMENT_METHODS[p.method] || p.method}</span>
                          {p.date && (() => {
                            const isFuture = p.date > todayISO();
                            return (
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${isFuture ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-muted text-muted-foreground border-border/60'}`}>
                                {isFuture ? '📅 ' : ''}{formatDateBR(p.date)}{isFuture ? ' · previsto' : ''}
                              </span>
                            );
                          })()}
                        </div>
                        {p.method === 'TRADE' && p.trade_vehicle && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                            <Car className="h-3 w-3 shrink-0" />
                            {p.trade_vehicle.brand} {p.trade_vehicle.model}{p.trade_vehicle.version ? ' ' + p.trade_vehicle.version : ''} · Placa {p.trade_vehicle.plate}
                          </div>
                        )}
                        {p.method === 'TRADE' && !p.trade_vehicle && p.trade_vehicle_label && (
                          <div className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                            <Car className="h-3 w-3 shrink-0" />
                            {p.trade_vehicle_label}
                          </div>
                        )}
                        {p.notes && p.method !== 'TRADE' && <div className="text-xs text-muted-foreground truncate">{p.notes}</div>}
                        {Number(p.amount) < 0 && (
                          <div className="text-[10px] font-medium text-red-700 bg-red-50 px-1.5 py-0.5 rounded border border-red-200 w-fit">
                            💰 Devolução ao comprador
                          </div>
                        )}
                      </div>
                      <div className={`text-sm font-semibold tabular shrink-0 ${Number(p.amount) < 0 ? 'text-red-600' : ''}`}>{formatBRL(p.amount)}</div>
                      {p.method !== 'TRADE' && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingPaymentIdx(idx);
                            setPaymentDialogMethod(p.method);
                            setPaymentDialogOpen(true);
                          }}
                          data-testid="payment-edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {p.method === 'TRADE' && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setEditingTradeIdx(idx);
                            setTradeDialogOpen(true);
                          }}
                          data-testid="payment-edit"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {/* Allow deletion in new sale mode, or for non-TRADE in edit mode */}
                      {(!editMode || p.method !== 'TRADE') && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removePayment(idx)} data-testid="payment-remove">
                          <Trash2 className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex mt-3">
                <Button type="button" size="sm" variant="outline" onClick={() => { setEditingPaymentIdx(null); setPaymentDialogMethod('PIX'); setPaymentDialogOpen(true); }} data-testid="add-payment-button">
                  <Plus className="h-4 w-4 mr-1.5" /> Adicionar forma de pagamento
                </Button>
              </div>

              {payments.length > 0 && (
                <div className={`mt-3 rounded-lg p-3 text-sm flex items-center justify-between ${paymentsValid ? 'bg-[hsl(var(--success-bg))] text-[hsl(var(--success))]' : 'bg-[hsl(var(--danger-bg))] text-[hsl(var(--danger))]'}`} data-testid="payments-summary">
                  <div>
                    <div className="font-medium">Total das formas de pagamento</div>
                    <div className="text-xs">
                      {paymentsValid ? '✓ Confere com o valor da venda' : `Diferença: ${formatBRL(remaining)} ${remaining > 0 ? '(falta)' : '(sobra)'}`}
                    </div>
                  </div>
                  <div className="font-semibold tabular text-base">{formatBRL(totalPayments)}</div>
                </div>
              )}
            </div>

            <Separator />

            <div>
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Comprador (opcional)</div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowBuyerPicker(true)}
                  data-testid="sell-buyer-picker-trigger"
                >
                  <Search className="h-3.5 w-3.5 mr-1.5" /> Selecionar cadastrado
                </Button>
              </div>
              {form.buyer_person_id && (
                <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 flex items-center gap-2 text-sm mb-3">
                  <UserCircle2 className="h-4 w-4 text-blue-700 shrink-0" />
                  <span className="text-blue-900 flex-1 min-w-0 truncate">
                    Vinculado ao cadastro de <strong>{form.buyer_name || 'pessoa'}</strong>
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setForm((f) => ({ ...f, buyer_person_id: '' }))}
                    title="Desvincular do cadastro"
                    data-testid="sell-buyer-clear-link"
                  >
                    <X className="h-3.5 w-3.5 text-blue-700" />
                  </Button>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Nome / Razão social</Label>
                  <Input value={form.buyer_name} onChange={(e) => setForm((f) => ({ ...f, buyer_name: e.target.value }))} data-testid="sell-buyer-name" />
                </div>
                <div>
                  <Label>CPF / CNPJ</Label>
                  <DocumentInput value={form.buyer_document} onChange={(v) => setForm((f) => ({ ...f, buyer_document: v }))} data-testid="sell-buyer-document" />
                </div>
                <div>
                  <Label>Telefone</Label>
                  <PhoneInput value={form.buyer_phone} onChange={(v) => setForm((f) => ({ ...f, buyer_phone: v }))} data-testid="sell-buyer-phone" />
                </div>
                <div>
                  <Label>E-mail</Label>
                  <Input type="email" value={form.buyer_email} onChange={(e) => setForm((f) => ({ ...f, buyer_email: e.target.value }))} />
                </div>
              </div>

              <div className="pt-3 border-t border-border/60">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Endereço do comprador</div>
                <AddressFields
                  value={form}
                  onChange={(next) => setForm((f) => ({ ...f, ...next }))}
                  testIdPrefix="sell-buyer-addr"
                />
              </div>

              <div className="pt-3 border-t border-border/60">
                <Label>Observações do negócio</Label>
                <div className="text-xs text-muted-foreground mb-1.5">Estas informações serão impressas no contrato, logo abaixo do chassi.</div>
                <Textarea
                  rows={3}
                  value={form.sale_notes}
                  onChange={(e) => setForm((f) => ({ ...f, sale_notes: e.target.value }))}
                  data-testid="sell-sale-notes"
                  placeholder="Ex.: garantia estendida de 60 dias para motor e câmbio, entrega prevista para 15/06, etc."
                />
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
            {payments.length > 0 && !paymentsValid && (
              <div className="text-xs text-[hsl(var(--danger))] font-medium mr-auto flex items-center gap-1.5" data-testid="sell-validation-msg">
                <AlertCircle className="h-3.5 w-3.5" />
                Soma dos pagamentos difere do valor da venda em {formatBRL(Math.abs(remaining))}.
              </div>
            )}
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button
              onClick={submit}
              disabled={submitting || (payments.length > 0 && !paymentsValid) || !form.sale_price || Number(form.sale_price) <= 0}
              className="bg-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/90"
              data-testid="sell-confirm-button"
            >
              {submitting ? 'Salvando...' : (editMode ? 'Salvar alterações' : 'Confirmar venda')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TradeVehicleDialog
        open={tradeDialogOpen}
        onOpenChange={(o) => { setTradeDialogOpen(o); if (!o) setEditingTradeIdx(null); }}
        sellerVehicle={reservation}
        salePrice={Number(form.sale_price || 0)}
        totalExpenses={(expenses || []).reduce((s, e) => s + (e.amount || 0), 0)}
        partners={partners || []}
        initial={editingTrade}
        onConfirm={onTradeConfirm}
      />

      <PaymentDialog
        open={paymentDialogOpen}
        onOpenChange={(o) => { setPaymentDialogOpen(o); if (!o) setEditingPaymentIdx(null); }}
        initialMethod={paymentDialogMethod}
        initial={editingPaymentIdx !== null ? payments[editingPaymentIdx] : null}
        suggestedAmount={editingPaymentIdx !== null ? 0 : Math.max(0, remaining)}
        defaultDate={form.sale_date}
        onConfirm={onPaymentConfirm}
        onRequestTrade={(amount) => {
          // User selected TRADE in payment dialog - hand off to trade dialog
          setPaymentDialogOpen(false);
          setEditingTradeIdx(null);
          // store the amount as a hint for the trade dialog (optional)
          setTradeDialogOpen(true);
        }}
      />

      <PersonPickerDialog
        open={showBuyerPicker}
        onOpenChange={setShowBuyerPicker}
        selectedId={form.buyer_person_id}
        onSelect={(p) => {
          setForm((f) => ({
            ...f,
            buyer_person_id: p.id,
            buyer_name: p.name || '',
            buyer_document: p.document || '',
            buyer_phone: p.phone || '',
            buyer_email: p.email || '',
          }));
        }}
      />
    </>
  );
}


function ReserveDialog({ open, onOpenChange, vehicleId, reservation, onReserved }) {
  const [form, setForm] = useState({
    has_deposit: true,
    reservation_deposit: '',
    reservation_deposit_date: todayISO(),
    reservation_due_date: '',
    reservation_buyer_name: '',
    reservation_buyer_document: '',
    reservation_buyer_phone: '',
    reservation_buyer_email: '',
    reservation_notes: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      // Pre-fill from existing reservation if any (allow editing)
      setForm({
        has_deposit: reservation?.reservation_deposit > 0 || !reservation?.reservation_buyer_name,
        reservation_deposit: reservation?.reservation_deposit ?? '',
        reservation_deposit_date: reservation?.reservation_deposit_date || todayISO(),
        reservation_due_date: reservation?.reservation_due_date || '',
        reservation_buyer_name: reservation?.reservation_buyer_name || '',
        reservation_buyer_document: reservation?.reservation_buyer_document || '',
        reservation_buyer_phone: reservation?.reservation_buyer_phone || '',
        reservation_buyer_email: reservation?.reservation_buyer_email || '',
        reservation_notes: reservation?.reservation_notes || '',
      });
    }
  }, [open, reservation]);

  const submit = async () => {
    if (form.has_deposit && (!form.reservation_deposit || Number(form.reservation_deposit) <= 0)) {
      toast.error('Informe o valor do sinal recebido (ou desmarque "Sinal recebido")');
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/vehicles/${vehicleId}/reserve`, {
        reservation_deposit: form.has_deposit ? Number(form.reservation_deposit) : 0,
        reservation_deposit_date: form.has_deposit ? form.reservation_deposit_date : null,
        reservation_due_date: form.reservation_due_date || null,
        reservation_buyer_name: form.reservation_buyer_name.trim() || null,
        reservation_buyer_document: form.reservation_buyer_document || null,
        reservation_buyer_phone: form.reservation_buyer_phone || null,
        reservation_buyer_email: form.reservation_buyer_email.trim() || null,
        reservation_notes: form.reservation_notes.trim() || null,
      });
      toast.success('Veículo reservado!');
      onOpenChange(false);
      onReserved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao reservar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bookmark className="h-5 w-5 text-amber-700" /> Reservar veículo
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
          <div className="rounded-lg border border-border/70 bg-muted/30 p-3 space-y-3">
            <label className="flex items-start gap-2 cursor-pointer" data-testid="reserve-has-deposit">
              <input
                type="checkbox"
                checked={form.has_deposit}
                onChange={(e) => setForm((f) => ({ ...f, has_deposit: e.target.checked, reservation_deposit: e.target.checked ? f.reservation_deposit : '' }))}
                className="mt-0.5 h-4 w-4 rounded border-border accent-[hsl(var(--primary))]"
              />
              <div className="text-sm">
                <div className="font-medium">Recebeu sinal de reserva?</div>
                <div className="text-xs text-muted-foreground">Se sim, o valor será exibido na DRE e no Resumo Financeiro como adiantamento em caixa.</div>
              </div>
            </label>
            {form.has_deposit && (
              <div className="grid grid-cols-2 gap-3 pl-6">
                <div>
                  <Label>Valor do sinal *</Label>
                  <CurrencyInput
                    value={form.reservation_deposit}
                    onChange={(v) => setForm((f) => ({ ...f, reservation_deposit: v }))}
                    data-testid="reserve-deposit-input"
                  />
                </div>
                <div>
                  <Label>Data do sinal</Label>
                  <Input
                    type="date"
                    value={form.reservation_deposit_date}
                    onChange={(e) => setForm((f) => ({ ...f, reservation_deposit_date: e.target.value }))}
                    data-testid="reserve-deposit-date-input"
                  />
                </div>
              </div>
            )}
            <div className="pl-6">
              <Label>Prazo da reserva</Label>
              <Input
                type="date"
                value={form.reservation_due_date}
                onChange={(e) => setForm((f) => ({ ...f, reservation_due_date: e.target.value }))}
                data-testid="reserve-due-date-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Data até quando a reserva é válida.</p>
            </div>
          </div>

          <Separator />

          <div>
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">Reservante</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Nome / Razão social</Label>
                <Input
                  value={form.reservation_buyer_name}
                  onChange={(e) => setForm((f) => ({ ...f, reservation_buyer_name: e.target.value }))}
                  data-testid="reserve-buyer-name"
                />
              </div>
              <div>
                <Label>CPF / CNPJ</Label>
                <DocumentInput
                  value={form.reservation_buyer_document}
                  onChange={(v) => setForm((f) => ({ ...f, reservation_buyer_document: v }))}
                  data-testid="reserve-buyer-document"
                />
              </div>
              <div>
                <Label>Telefone</Label>
                <PhoneInput
                  value={form.reservation_buyer_phone}
                  onChange={(v) => setForm((f) => ({ ...f, reservation_buyer_phone: v }))}
                  data-testid="reserve-buyer-phone"
                />
              </div>
              <div>
                <Label>E-mail</Label>
                <Input
                  type="email"
                  value={form.reservation_buyer_email}
                  onChange={(e) => setForm((f) => ({ ...f, reservation_buyer_email: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div>
            <Label>Observações da reserva</Label>
            <Textarea
              rows={2}
              value={form.reservation_notes}
              onChange={(e) => setForm((f) => ({ ...f, reservation_notes: e.target.value }))}
              placeholder='Ex: "Vai buscar financiamento em 5 dias, deixou R$ 2.000 de sinal"'
              data-testid="reserve-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="reserve-confirm-button"
          >
            {submitting ? 'Salvando...' : 'Confirmar reserva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============== CHECKLIST TAB ==============
function ChecklistTab({ vehicleId }) {
  const [checklist, setChecklist] = useState({ items: [], total: 0, done: 0, progress_pct: 0 });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customForm, setCustomForm] = useState({ name: '', due_date: '' });

  const loadChecklist = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/vehicles/${vehicleId}/checklist`);
      setChecklist(data);
    } catch (err) {
      toast.error('Erro ao carregar checklist');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadChecklist(); }, [vehicleId]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/vehicles/${vehicleId}/checklist/sync-templates`);
      toast.success(data.added > 0 ? `${data.added} item(ns) sincronizado(s)` : 'Checklist já está sincronizado');
      loadChecklist();
    } catch (err) {
      toast.error('Erro ao sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const handleToggle = async (item) => {
    try {
      await api.patch(`/checklist-items/${item.id}`, { is_done: !item.is_done });
      loadChecklist();
    } catch (err) {
      toast.error('Erro ao atualizar item');
    }
  };

  const handleAddCustom = async () => {
    if (!customForm.name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }
    try {
      await api.post(`/vehicles/${vehicleId}/checklist`, {
        name: customForm.name.trim(),
        due_date: customForm.due_date || null,
      });
      toast.success('Item adicionado');
      setShowAddCustom(false);
      setCustomForm({ name: '', due_date: '' });
      loadChecklist();
    } catch (err) {
      toast.error('Erro ao adicionar item');
    }
  };

  const handleDeleteCustom = async (itemId) => {
    if (!window.confirm('Excluir este item?')) return;
    try {
      await api.delete(`/checklist-items/${itemId}`);
      toast.success('Item excluído');
      loadChecklist();
    } catch (err) {
      toast.error('Erro ao excluir');
    }
  };

  if (loading) {
    return <Skeleton className="h-64 rounded-xl" />;
  }

  return (
    <>
      <Card className="border-border/70">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-muted-foreground" /> Checklist
              </CardTitle>
              {checklist.total > 0 && (
                <div className="flex items-center gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded-full ${checklist.progress_pct === 100 ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                    {checklist.done} / {checklist.total} ({checklist.progress_pct}%)
                  </span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleSync} disabled={syncing} data-testid="checklist-sync">
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sincronizando...' : 'Sincronizar templates'}
              </Button>
              <Button size="sm" onClick={() => setShowAddCustom(true)} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="checklist-add-custom">
                <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar item
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {checklist.items.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>Nenhum item no checklist.</p>
              <p className="text-xs mt-1">Clique em "Sincronizar templates" para importar os itens padrão.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {checklist.items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 p-3 rounded-lg border border-border/70 hover:bg-muted/30 transition-colors"
                  data-testid="checklist-item"
                >
                  <button
                    onClick={() => handleToggle(item)}
                    className="mt-0.5 shrink-0 focus:outline-none"
                  >
                    {item.is_done ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    ) : (
                      <Circle className="h-5 w-5 text-muted-foreground" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${item.is_done ? 'line-through text-muted-foreground' : ''}`}>
                      {item.name}
                      {item.is_custom && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-800">CUSTOM</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-1 text-[11px] text-muted-foreground">
                      {item.due_date && (
                        <span className={`px-1.5 py-0.5 rounded ${new Date(item.due_date) < new Date() && !item.is_done ? 'bg-red-100 text-red-800' : 'bg-muted'}`}>
                          Prazo: {formatDateBR(item.due_date)}
                        </span>
                      )}
                      {item.done_at && (
                        <span className="text-green-700">✓ Concluído em {formatDateBR(item.done_at)}</span>
                      )}
                    </div>
                  </div>
                  {item.is_custom && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleDeleteCustom(item.id)}
                      className="shrink-0"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-[hsl(var(--danger))]" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showAddCustom} onOpenChange={setShowAddCustom}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar item customizado</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome do item *</Label>
              <Input
                value={customForm.name}
                onChange={(e) => setCustomForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Ex: Troca de óleo"
                data-testid="custom-item-name"
              />
            </div>
            <div>
              <Label>Prazo</Label>
              <Input
                type="date"
                value={customForm.due_date}
                onChange={(e) => setCustomForm((f) => ({ ...f, due_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCustom(false)}>Cancelar</Button>
            <Button onClick={handleAddCustom} className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90">
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </>
  );
}




// Post-Sale Expense Dialog (inline component like ExpenseDialog)
function PostSaleExpenseDialogInline({ open, onOpenChange, vehicleId, onSaved }) {
  const [form, setForm] = useState({
    description: '',
    amount: '',
    expense_date: todayISO(),
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        description: '',
        amount: '',
        expense_date: todayISO(),
      });
    }
  }, [open]);

  const submit = async () => {
    if (!form.description.trim()) {
      toast.error('Informe a descrição da despesa');
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error('Informe o valor da despesa');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        description: form.description.trim(),
        amount: Number(form.amount),
        expense_date: form.expense_date,
      };
      const res = await api.post(`/vehicles/${vehicleId}/post-sale-expenses`, payload);
      toast.success('Despesa pós-venda registrada e distribuída entre os sócios');
      
      if (res.data.distributions && res.data.distributions.length > 0) {
        const summary = res.data.distributions.map(d => 
          `${d.partner_name}: ${formatBRL(d.expense_amount)} (${d.share_pct}%)`
        ).join(', ');
        console.log('Distribuição:', summary);
      }
      
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao registrar despesa');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            Registrar Despesa Pós-Venda
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-2">
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm">
            <div className="font-medium text-amber-900 mb-1">ℹ️ Distribuição Automática</div>
            <div className="text-amber-800">
              Esta despesa será distribuída automaticamente entre os sócios proporcionalmente à participação deles na venda deste veículo.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Data *</Label>
              <Input
                type="date"
                value={form.expense_date}
                onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                data-testid="post-sale-expense-date"
              />
            </div>
            <div>
              <Label>Valor *</Label>
              <CurrencyInput
                value={form.amount}
                onChange={(v) => setForm({ ...form, amount: v })}
                placeholder="0,00"
              />
            </div>
          </div>

          <div>
            <Label>Descrição *</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Ex: Conserto de motor, Problema de pintura"
              rows={3}
              maxLength={500}
              data-testid="post-sale-expense-description"
            />
            <div className="text-xs text-muted-foreground mt-1">
              {form.description.length}/500 caracteres
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={submitting}>
            {submitting ? 'Registrando...' : 'Registrar Despesa'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
